const STORAGE_KEY = 'grapesjs-cross-tab-clipboard';
const STORAGE_TIMESTAMP_KEY = 'grapesjs-cross-tab-clipboard-timestamp';

const CLIPBOARD_MARKER = '__grapesjs-cross-tab-clipboard__';

const generateUniqueId = () => {
    return 'gjs-' + Math.random().toString(36).substr(2, 9);
};

const remapSelectorIds = (selectorText, idMap) => {
    if (typeof selectorText !== 'string' || !selectorText.trim()) return '';
    let nextSelector = selectorText;
    const orderedEntries = Object.entries(idMap || {}).sort(
        ([oldIdA], [oldIdB]) => oldIdB.length - oldIdA.length
    );
    orderedEntries.forEach(([oldId, newId]) => {
        const regex = new RegExp(`#${oldId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
        nextSelector = nextSelector.replace(regex, `#${newId}`);
    });
    return nextSelector;
};

const writeToSystemClipboard = async (text) => {
    if (typeof navigator === 'undefined' || !navigator.clipboard || !navigator.clipboard.writeText) {
        return false;
    }
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        console.warn('Clipboard write failed, falling back to localStorage only:', error);
        return false;
    }
};

const readFromSystemClipboard = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard || !navigator.clipboard.readText) {
        return null;
    }
    try {
        const text = await navigator.clipboard.readText();
        if (!text) return null;
        const parsed = JSON.parse(text);
        if (parsed && parsed[CLIPBOARD_MARKER]) {
            return parsed.payload;
        }
        return null;
    } catch (error) {
        return null;
    }
};

const collectComponentIds = (component, idSet = new Set()) => {
    if (!component) return idSet;
    const id = component?.getId();
    if (id) idSet.add(id);
    if (component.components && typeof component.components === 'function') {
    const children = component.components();
    if (children && children.length) {
      children.forEach(child => collectComponentIds(child, idSet));
    }
  }

    return idSet;
};

const getSelectedComponents = (editor) => {
    const selectedAll = editor.getSelectedAll?.();
    if (Array.isArray(selectedAll) && selectedAll.length) return selectedAll;
    if (Array.isArray(selectedAll?.models) && selectedAll.models.length) return selectedAll.models;
    if (selectedAll && typeof selectedAll.forEach === 'function') {
        const arr = [];
        selectedAll.forEach((cmp) => arr.push(cmp));
        if (arr.length) return arr;
    }

    const selected = editor.getSelected?.();
    return selected ? [selected] : [];
};

const getStylesForIds = (editor, ids) => {
    const cssComposer = editor.CssComposer;
    const deviceManager = editor.DeviceManager;
    const allStyles = [];

    const devices = deviceManager ? deviceManager.getAll() : [];
    const currentDevice = deviceManager ? deviceManager.getSelected() : null;

    const getRulesForDevice = (device) => {
        // Temporarily switch to the device if needed to get its rules
        if (device && deviceManager) {
            deviceManager.select(device);
        }

        const rules = cssComposer.getAll();
        return rules.filter(rule => {
            const selector = rule.getSelectorsString ? rule.getSelectorsString() : '';
            return selector && ids.some(id => selector.includes(`#${id}`));
        }).map(rule => {
            let atRule = '';
            let atRuleType = '';
            let atRuleParams = '';

            if (rule.getAtRule && typeof rule.getAtRule === 'function') {
                atRule = rule.getAtRule() || '';
            } else if (rule.get('atRule')) {
                atRule = rule.get('atRule') || '';
            }

            if (!atRule && rule.get && rule.get('mediaText')) {
                atRule = rule.get('mediaText');
            }

            if (atRule) {
                const match = atRule.match(/@(\w+)\s*(.*)/);
                if (match) {
                    atRuleType = match[1];
                    atRuleParams = match[2] || '';
                }
            }

            const deviceName = device ? (device.get ? device.get('name') : device.name) : '';
            const deviceWidth = device ? (device.get ? device.get('width') : device.width) : '';
            const selectorText = rule.getSelectorsString ? rule.getSelectorsString() : '';

            return {
                selectorText,
                style: rule.getStyle ? rule.getStyle() : {},
                mediaText: atRule,
                atRuleType: atRuleType,
                atRuleParams: atRuleParams,
                deviceName: deviceName,
                deviceWidth: deviceWidth,
            };
        });
    };

    if (devices && devices.length > 0) {
        devices.forEach(device => {
            const deviceRules = getRulesForDevice(device);
            allStyles.push(...deviceRules);
        });
    }

    const defaultRules = cssComposer.getAll().filter(rule => {
        const selector = rule.getSelectorsString ? rule.getSelectorsString() : '';
        return selector && ids.some(id => selector.includes(`#${id}`));
    }).map(rule => {
        let atRule = '';
        let atRuleType = '';
        let atRuleParams = '';
        if (rule.getAtRule && typeof rule.getAtRule === 'function') {
            atRule = rule.getAtRule() || '';
        } else if (rule.get('atRule')) {
            atRule = rule.get('atRule') || '';
        }

        if (!atRule && rule.get && rule.get('mediaText')) {
            atRule = rule.get('mediaText');
        }

        if (atRule) {
            const match = atRule.match(/@(\w+)\s*(.*)/);
            if (match) {
                atRuleType = match[1];
                atRuleParams = match[2] || '';
            }
        }
        const selectorText = rule.getSelectorsString ? rule.getSelectorsString() : '';
        return {
            selectorText,
            style: rule.getStyle ? rule.getStyle() : {},
            mediaText: atRule,
            atRuleType: atRuleType,
            atRuleParams: atRuleParams,
        };
    });

    // Merge and deduplicate
    const styleMap = new Map();
    [...allStyles, ...defaultRules].forEach(style => {
        const key = JSON.stringify({
            selectorText: style.selectorText || '',
            atRuleType: style.atRuleType,
            atRuleParams: style.atRuleParams,
        });
        if (!styleMap.has(key)) {
            styleMap.set(key, style);
        }
    });

    // Restore original device selection
    if (currentDevice && deviceManager) {
        deviceManager.select(currentDevice);
    }

    return Array.from(styleMap.values());
};

const updateComponentIdsAndMap = (component, idMap = {}) => {
    if (!component || typeof component !== 'object') return idMap;
    const attrId = component?.attributes?.id;
    if (attrId) {
        if (!idMap[attrId]) idMap[attrId] = generateUniqueId();
        component.attributes = { ...(component.attributes || {}), id: idMap[attrId] };
    }

    if (component.components && Array.isArray(component.components)) {
        component.components.forEach((child) => updateComponentIdsAndMap(child, idMap));
    }
    return idMap;
};

const updateStylesWithNewIds = (styles, idMap) => {
    if (!Array.isArray(styles)) {
        return styles;
    }

    return styles.map((styleRule) => {
        let updatedRule = { ...styleRule };
        const selectorText = typeof updatedRule.selectorText === 'string'
            ? updatedRule.selectorText
            : (Array.isArray(updatedRule.selectors)
                ? updatedRule.selectors.filter((sel) => typeof sel === 'string').join(', ')
                : '');

        updatedRule.selectorText = remapSelectorIds(selectorText, idMap);

        return updatedRule;
    });
};

export const crossTabCopyPaste = (editor) => {
    const { Commands, Panels } = editor;
    Commands.add('cross-tab-copy', {
        run: async (editor, sender) => {
            const selectedComponents = getSelectedComponents(editor);

            if (!selectedComponents.length) {
                editor.Modal.open({
                    title: 'No Component Selected',
                    content: 'Please select a component to copy.',
                    attributes: { class: 'gjs-modal-sm' },
                });
                return;
            }

            try {
                const componentData =
                    selectedComponents.length === 1
                        ? selectedComponents[0].toJSON()
                        : selectedComponents.map((cmp) => cmp.toJSON());
                const idSet = new Set();
                selectedComponents.forEach((cmp) => collectComponentIds(cmp, idSet));
                const componentIds = [...idSet];
                const relatedStyles = getStylesForIds(editor, componentIds);
                const clipboardData = {
                    component: componentData,
                    styles: relatedStyles,
                };
                const serialized = JSON.stringify(clipboardData);
                try {
                    localStorage.setItem(STORAGE_KEY, serialized);
                    localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
                } catch (storageError) {
                    console.warn('localStorage write failed:', storageError);
                }
                const wroteToClipboard = await writeToSystemClipboard(
                    JSON.stringify({ [CLIPBOARD_MARKER]: true, payload: clipboardData })
                );

                editor.Modal.open({
                    title: 'Component Copied',
                    content: wroteToClipboard
                        ? 'Component has been copied to clipboard..'
                        : 'Component has been copied to clipboard.',
                    attributes: { class: 'gjs-modal-sm' },
                });


            } catch (error) {
                console.error('Error copying component:', error);
                editor.Modal.open({
                    title: 'Copy Failed',
                    content: 'Failed to copy component. Please try again.',
                    attributes: { class: 'gjs-modal-sm' },
                });
            }
        },
    });

    Commands.add('cross-tab-paste', {
        run: async (editor, sender) => {
            try {
                let clipboardDataParsed = await readFromSystemClipboard();
                if (!clipboardDataParsed) {
                    const localData = localStorage.getItem(STORAGE_KEY);
                    if (localData) {
                        clipboardDataParsed = JSON.parse(localData);
                    }
                }

                if (!clipboardDataParsed) {
                    editor.Modal.open({
                        title: 'Clipboard Empty',
                        content: 'No component found in clipboard. Copy a component first.',
                        attributes: { class: 'gjs-modal-sm' },
                    });
                    return;
                }

                let componentData, stylesToAdd;
                if (clipboardDataParsed.component) {
                    componentData = clipboardDataParsed.component;
                    stylesToAdd = clipboardDataParsed.styles || [];
                } else {
                    componentData = clipboardDataParsed;
                    stylesToAdd = [];
                }

                if (!componentData || typeof componentData !== 'object') {
                    throw new Error('Invalid component data structure');
                }

                const clonedComponent = JSON.parse(JSON.stringify(componentData));
                const clonedStyles = JSON.parse(JSON.stringify(stylesToAdd));

                const idMap = {};
                if (Array.isArray(clonedComponent)) {
                    clonedComponent.forEach((component) => updateComponentIdsAndMap(component, idMap));
                } else {
                    updateComponentIdsAndMap(clonedComponent, idMap);
                }

                const updatedStyles = updateStylesWithNewIds(clonedStyles, idMap);
                const componentsToInsert = Array.isArray(clonedComponent) ? clonedComponent : [clonedComponent];

                const selected = editor.getSelected();
                let newComponents = [];
                const wrapper = editor.getWrapper();

                if (selected && selected !== wrapper) {
                    const parent = selected.parent && selected.parent();
                    const parentComponents = parent && parent.components ? parent.components() : null;
                    const selectedIndex = parentComponents ? parentComponents.indexOf(selected) : -1;

                    if (parentComponents && selectedIndex > -1) {
                        componentsToInsert.forEach((cmp, idx) => {
                            const added = parentComponents.add(cmp, { at: selectedIndex + 1 + idx });
                            newComponents.push(Array.isArray(added) ? added[0] : added);
                        });
                    }
                }

                if (!newComponents.length) {
                    componentsToInsert.forEach((cmp) => {
                        const added = wrapper.components().add(cmp);
                        newComponents.push(Array.isArray(added) ? added[0] : added);
                    });
                }

                if (updatedStyles && updatedStyles.length > 0) {
                    const cssComposer = editor.CssComposer;
                    const currentRules = cssComposer.getAll();

                    updatedStyles.forEach(newStyleRule => {
                        const newSelector = (newStyleRule.selectorText || '').trim();
                        if (!newSelector) return;

                        // Check if rule with same selector and media query already exists
                        const exists = currentRules.some(existingRule => {
                            const existingSelector = existingRule.getSelectorsString();
                            const existingAtRule = existingRule.getAtRule ? existingRule.getAtRule() : '';
                            const newAtRule = newStyleRule.mediaText || '';

                            return existingSelector === newSelector && existingAtRule === newAtRule;
                        });

                        if (!exists) {
                            try {
                                const ruleOptions = {};

                                if (newStyleRule.atRuleType && newStyleRule.atRuleParams) {
                                    ruleOptions.atRuleType = newStyleRule.atRuleType;
                                    ruleOptions.atRuleParams = newStyleRule.atRuleParams;
                                } else if (newStyleRule.mediaText) {
                                    const match = newStyleRule.mediaText.match(/@(\w+)\s*(.*)/);
                                    if (match) {
                                        ruleOptions.atRuleType = match[1];
                                        ruleOptions.atRuleParams = match[2] || '';
                                    }
                                }

                                cssComposer.setRule(
                                    newSelector,
                                    newStyleRule.style || {},
                                    ruleOptions
                                );
                            } catch (error) {
                                console.error('Error adding style rule:', error, newStyleRule);
                            }
                        } else {
                            console.log('Style rule already exists, skipping:', newSelector, newStyleRule.atRuleParams ? `(${newStyleRule.atRuleParams})` : '');
                        }
                    });

                    editor.refresh();
                } else {
                    console.log('No styles to add');
                }
                if (newComponents.length === 1) {
                    editor.select(newComponents[0]);
                } else if (newComponents.length > 1) {
                    editor.selectAdd(newComponents);
                }
                editor.Modal.open({
                    title: 'Component Pasted',
                    content: 'Component has been pasted successfully.',
                    attributes: { class: 'gjs-modal-sm' },
                });
                setTimeout(() => {
                    editor.Modal.close();
                }, 2000);
            } catch (error) {
                console.error('Error pasting component:', error);
                editor.Modal.open({
                    title: 'Paste Failed',
                    content: `Failed to paste component: ${error.message || 'The clipboard data may be invalid.'}`,
                    attributes: { class: 'gjs-modal-sm' },
                });
            }
        },
    });

    const checkClipboard = () => {
        const clipboardData = localStorage.getItem(STORAGE_KEY);
        const pasteBtn = Panels.getButton('options', 'cross-tab-paste');

        if (pasteBtn) {
            if (clipboardData) {
                pasteBtn.set('active', true);
            } else {
                pasteBtn.set('active', false);
            }
        }
    };

    window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY || e.key === STORAGE_TIMESTAMP_KEY) {
            // checkClipboard();
        }
    });

    editor.on('load', () => {
        setTimeout(() => {
            // checkClipboard();
        }, 100);
    });
};
